/* global BigInt */
/**
 * Centralized Money Utility with Int64 Support
 *
 * IMPORTANT: All money in this system is stored and transmitted in CENTS as STRINGS.
 * - Server stores balance, bets, payouts in CENTS (NUMERIC in database, arbitrary precision)
 * - WebSocket messages transmit money as STRINGS for arbitrary precision
 * - Frontend receives strings and uses BigInt for arithmetic
 *
 * JavaScript BigInt supports arbitrary precision integers — no upper limit.
 *
 * Example: $100.00 = "10000" (string representing cents)
 */

// Tier list for number formatting — ordered largest to smallest.
// Each entry: [threshold in cents (BigInt), suffix string]
// Covers up to Vigintillion (10^63). Scientific notation above that.
const MONEY_TIERS = [
  [10n ** 65n, 'Vg'],    // Vigintillion   $10^63
  [10n ** 62n, 'NoDc'],  // Novemdecillion $10^60
  [10n ** 59n, 'OcDc'],  // Octodecillion  $10^57
  [10n ** 56n, 'SpDc'],  // Septendecillion$10^54
  [10n ** 53n, 'SxDc'],  // Sexdecillion   $10^51
  [10n ** 50n, 'QiDc'],  // Quindecillion  $10^48
  [10n ** 47n, 'QaDc'],  // Quattuordec.   $10^45
  [10n ** 44n, 'TDc'],   // Tredecillion   $10^42
  [10n ** 41n, 'DDc'],   // Duodecillion   $10^39
  [10n ** 38n, 'UDc'],   // Undecillion    $10^36
  [10n ** 35n, 'Dc'],    // Decillion      $10^33
  [10n ** 32n, 'No'],    // Nonillion      $10^30
  [10n ** 29n, 'Oc'],    // Octillion      $10^27
  [10n ** 26n, 'Sp'],    // Septillion     $10^24
  [10n ** 23n, 'Sx'],    // Sextillion     $10^21
  [10n ** 20n, 'Qi'],    // Quintillion    $10^18
  [10n ** 17n, 'Q'],     // Quadrillion    $10^15
  [10n ** 14n, 'T'],     // Trillion       $10^12
  [10n ** 11n, 'B'],     // Billion        $10^9
  [10n ** 8n,  'M'],     // Million        $10^6
  [10n ** 5n,  'K'],     // Thousand       $10^3
];

// Highest tier threshold (for sci-notation fallback check)
const _MAX_TIER = MONEY_TIERS[0][0] * 1000n;

// Commonly used thresholds (still needed for comparisons in formatMoney/formatBetInput)
const _T  = 10n ** 14n;
const _M  = 10n ** 8n;
const _SCALE = 1000000n;

const VALID_INT_RE = /^-?\d+$/;
const NON_NUMERIC_RE = /[^0-9.]/g;

const absBigInt = (value) => (value < 0n ? -value : value);

const formatGroupedInteger = (value) => {
  const digits = value.toString();
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    const remaining = digits.length - i;
    out += digits[i];
    if (remaining > 1 && remaining % 3 === 1) {
      out += ',';
    }
  }
  return out;
};

const formatScientificFromCents = (centsBig, prefix = '$') => {
  const abs = absBigInt(centsBig);
  const dollarStr = (abs / 100n).toString();
  const exponent = Math.max(0, dollarStr.length - 1);
  const fraction = dollarStr.slice(1, 3).padEnd(2, '0').replace(/0+$/, '');
  const mantissa = fraction.length > 0 ? `${dollarStr[0]}.${fraction}` : dollarStr[0];
  return `${centsBig < 0n ? '-' : ''}${prefix}${mantissa}e${exponent}`;
};

const formatTierValue = (centsBig, threshold, decimals = 1, trimZeros = false) => {
  const scale = 10n ** BigInt(decimals);
  const scaled = (centsBig * scale + threshold / 2n) / threshold;
  const whole = scaled / scale;
  const fraction = (scaled % scale).toString().padStart(decimals, '0');

  if (decimals === 0) {
    return whole.toString();
  }

  if (trimZeros) {
    const trimmed = fraction.replace(/0+$/, '');
    return trimmed.length > 0 ? `${whole}.${trimmed}` : whole.toString();
  }

  return `${whole}.${fraction}`;
};

const formatPlainDollarAmount = (centsBig, showCents = true, withPrefix = true) => {
  const abs = absBigInt(centsBig);
  const dollars = abs / 100n;
  const centsPart = (abs % 100n).toString().padStart(2, '0');
  const sign = centsBig < 0n ? '-' : '';
  const prefix = withPrefix ? '$' : '';

  if (showCents) {
    return `${sign}${prefix}${formatGroupedInteger(dollars)}.${centsPart}`;
  }

  return `${sign}${prefix}${formatGroupedInteger(dollars)}`;
};

/**
 * Parse any money value to a string (for BigInt operations)
 * Handles null, undefined, numbers, and strings
 * @param {string|number|null|undefined} value - Money value
 * @returns {string} Normalized string representation
 */
export const parseMoney = (value) => {
  if (value === null || value === undefined) return '0';
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'number') {
    // Check for unsafe integer (precision loss)
    if (!Number.isSafeInteger(value)) {
    }
    return Math.floor(value).toString();
  }
  // Already a string - validate it's a valid integer
  const str = String(value).trim();
  if (!VALID_INT_RE.test(str)) {
    return '0';
  }
  return str;
};

/**
 * Parse Money string/value to JavaScript number (for calculations)
 * WARNING: Use only when you need arithmetic operations on money values.
 * Values above Number.MAX_SAFE_INTEGER (~9 quadrillion) will lose precision.
 * @param {string|number|bigint|null|undefined} value - Money value
 * @returns {number} Number value (0 for invalid input)
 */
export const parseMoneyToNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'bigint') {
    return Number(value);
  }
  const str = String(value).trim();
  const num = Number(str);
  if (isNaN(num)) {
    return 0;
  }
  return num;
};

/**
 * Add two money values (as strings)
 * @param {string} a - First amount
 * @param {string} b - Second amount
 * @returns {string} Sum as string
 */
export const addMoney = (a, b) => {
  return (BigInt(parseMoney(a)) + BigInt(parseMoney(b))).toString();
};

/**
 * Subtract money: a - b (as strings)
 * @param {string} a - Amount to subtract from
 * @param {string} b - Amount to subtract
 * @returns {string} Difference as string
 */
export const subtractMoney = (a, b) => {
  return (BigInt(parseMoney(a)) - BigInt(parseMoney(b))).toString();
};

/**
 * Multiply money by a decimal multiplier
 * Uses fixed-point math to avoid precision loss
 * @param {string} a - Amount in cents
 * @param {number} multiplier - Decimal multiplier
 * @returns {string} Product as string
 */
export const multiplyMoney = (a, multiplier) => {
  const scaledMultiplier = BigInt(Math.floor(multiplier * 1000000));
  return ((BigInt(parseMoney(a)) * scaledMultiplier) / _SCALE).toString();
};

/**
 * Compare two money values
 * @param {string} a - First amount
 * @param {string} b - Second amount
 * @returns {number} -1 if a < b, 0 if a == b, 1 if a > b
 */
export const compareMoney = (a, b) => {
  const aBig = BigInt(parseMoney(a));
  const bBig = BigInt(parseMoney(b));
  if (aBig < bBig) return -1;
  if (aBig > bBig) return 1;
  return 0;
};

/**
 * Check if money value is positive (> 0)
 * @param {string} a - Amount
 * @returns {boolean}
 */
export const isPositive = (a) => {
  return BigInt(parseMoney(a)) > 0n;
};

/**
 * Check if a >= b (has sufficient balance)
 * @param {string} balance - Current balance
 * @param {string} amount - Amount to check
 * @returns {boolean}
 */
export const hasSufficientBalance = (balance, amount) => {
  return BigInt(parseMoney(balance)) >= BigInt(parseMoney(amount));
};

/**
 * Double a money amount (STRING → STRING)
 * @param {string} cents - Amount in cents
 * @returns {string} Doubled amount as string
 */
export const doubleMoney = (cents) => {
  const big = BigInt(parseMoney(cents));
  return (big * 2n).toString();
};

/**
 * Halve a money amount (STRING → STRING)
 * Uses integer division (floors the result)
 * @param {string} cents - Amount in cents
 * @returns {string} Halved amount as string
 */
export const halfMoney = (cents) => {
  const big = BigInt(parseMoney(cents));
  return (big / 2n).toString();
};

/**
 * Get the minimum of two money amounts
 * @param {string} a - First amount
 * @param {string} b - Second amount
 * @returns {string} The smaller amount
 */
export const minMoney = (a, b) => {
  const bigA = BigInt(parseMoney(a));
  const bigB = BigInt(parseMoney(b));
  return (bigA < bigB ? bigA : bigB).toString();
};

/**
 * Get the maximum of two money amounts
 * @param {string} a - First amount
 * @param {string} b - Second amount
 * @returns {string} The larger amount
 */
export const maxMoney = (a, b) => {
  const bigA = BigInt(parseMoney(a));
  const bigB = BigInt(parseMoney(b));
  return (bigA > bigB ? bigA : bigB).toString();
};

/**
 * Clamp a money amount between min and max
 * @param {string} amount - Amount to clamp
 * @param {string} min - Minimum value
 * @param {string} max - Maximum value
 * @returns {string} Clamped amount
 */
export const clampMoney = (amount, min, max) => {
  return maxMoney(min, minMoney(amount, max));
};

/**
 * Parse a user input (dollars with decimals) to cents STRING
 * Handles formats like "123.45", "1,234.56", "100", etc.
 * @param {string} dollarInput - User input in dollars
 * @returns {string} Cents as string
 */
export const fromDollarInput = (dollarInput) => {
  if (!dollarInput || dollarInput === '') return '0';
  // Remove all non-numeric characters except decimal point
  const cleaned = String(dollarInput).replace(NON_NUMERIC_RE, '');
  const parts = cleaned.split('.');
  const dollars = parts[0] || '0';
  // Take only first 2 decimal digits, pad if needed
  const cents = (parts[1] || '00').slice(0, 2).padEnd(2, '0');
  // Use BigInt to avoid precision loss on large values
  const totalCents = BigInt(dollars) * 100n + BigInt(cents);
  return totalCents.toString();
};

/**
 * Format cents for bet input field display
 * Shows compact format for large values (>= $1M), regular format for smaller
 * @param {string|number} cents - Amount in cents
 * @returns {string} Formatted string for input display (without $ prefix)
 */
export const formatBetInput = (cents) => {
  const parsed = parseMoney(cents);
  const centsBig = BigInt(parsed);
  const abs = absBigInt(centsBig);

  // For values >= $1M (100M cents), use compact format without $
  if (abs >= _M) {
    // Beyond highest tier: scientific notation
    if (abs >= _MAX_TIER) {
      return formatScientificFromCents(centsBig, '').replace(/^\+?/, '');
    }
    // Walk tier list
    for (const [threshold, suffix] of MONEY_TIERS) {
      if (abs >= threshold) {
        return `${centsBig < 0n ? '-' : ''}${formatTierValue(abs, threshold, 1, false)}${suffix}`;
      }
    }
  }

  // For smaller values, show regular dollars with 2 decimals
  return formatPlainDollarAmount(centsBig, true, false);
};

/**
 * Format cents as a display string (e.g., "$1,234.56")
 * Handles int64 values safely
 * @param {string|number} cents - Amount in cents
 * @param {boolean} showCents - Whether to show cents (default: true)
 * @returns {string} Formatted string
 */
export const formatMoney = (cents, showCents = true) => {
  const parsed = parseMoney(cents);
  const centsBig = BigInt(parsed);
  const abs = absBigInt(centsBig);

  // For very large values, use compact format
  if (abs >= _T) {
    return formatMoneyCompact(parsed);
  }

  return formatPlainDollarAmount(centsBig, showCents, true);
};

/**
 * Format cents as a compact display string (e.g., "$1.2K", "$5.5M", "$2.3B", "$1.5T", "$2.1Q")
 * Supports arbitrary precision using BigInt. Beyond Decillion uses scientific notation.
 * @param {string|number} cents - Amount in cents
 * @returns {string} Compact formatted string
 */
export const formatMoneyCompact = (cents) => {
  const parsed = parseMoney(cents);
  const centsBig = BigInt(parsed);
  const abs = absBigInt(centsBig);

  // Handle negative values
  if (centsBig < 0n) {
    return '-' + formatMoneyCompact((-centsBig).toString());
  }

  // Beyond highest tier: scientific notation fallback
  if (abs >= _MAX_TIER) {
    return formatScientificFromCents(centsBig);
  }

  // Walk tier list (largest first)
  for (const [threshold, suffix] of MONEY_TIERS) {
    if (abs >= threshold) {
      return '$' + formatTierValue(abs, threshold, 1, false) + suffix;
    }
  }

  // Small enough for direct conversion
  return '$' + formatGroupedInteger(abs / 100n);
};

/**
 * Format cents for chip display (no $ prefix, compact)
 * Supports arbitrary precision. Beyond Decillion uses scientific notation.
 * @param {string|number} cents - Amount in cents
 * @returns {string} Chip label (e.g., "100", "2.5K", "10K", "5B", "1Q")
 */
export const formatChipLabel = (cents) => {
  const parsed = parseMoney(cents);
  const centsBig = BigInt(parsed);
  const abs = absBigInt(centsBig);

  // Beyond highest tier: scientific notation
  if (abs >= _MAX_TIER) {
    return formatScientificFromCents(centsBig, '').replace(/^\+?/, '');
  }

  // Walk tier list
  for (const [threshold, suffix] of MONEY_TIERS) {
    if (abs >= threshold) {
      return `${centsBig < 0n ? '-' : ''}${formatTierValue(abs, threshold, 1, true)}${suffix}`;
    }
  }

  return `${centsBig < 0n ? '-' : ''}${(abs / 100n).toString()}`;
};

/**
 * Check if amount is valid for betting
 * @param {string} cents - Amount in cents
 * @param {string} balance - Current balance in cents
 * @param {string} minBet - Minimum bet in cents (default: "100" = $1)
 * @returns {boolean}
 */
export const isValidBetAmount = (cents, balance, minBet = '100') => {
  try {
    const centsBig = BigInt(parseMoney(cents));
    const balanceBig = BigInt(parseMoney(balance));
    const minBig = BigInt(parseMoney(minBet));
    return centsBig >= minBig && centsBig <= balanceBig;
  } catch {
    return false;
  }
};

/**
 * Smart format: uses compact format for large values, full format for smaller
 * Shows full format under $1M, compact above
 * @param {string|number} cents - Amount in cents
 * @returns {string} Formatted string
 */
export const formatMoneySmart = (cents) => {
  const parsed = parseMoney(cents);
  const centsBig = BigInt(parsed);

  // Use absolute value for comparison to handle negative numbers correctly
  const absValue = centsBig < 0n ? -centsBig : centsBig;

  // Use compact format for $1M and above (by absolute value)
  if (absValue >= _M) {
    return formatMoneyCompact(parsed);
  }

  // Use full format for smaller amounts
  return formatMoney(parsed, true);
};

/**
 * Format production rate per second (e.g., "$1.5K/s")
 * @param {string|number} centsPerSecond - Production rate in cents per second
 * @returns {string} Formatted rate string
 */
export const formatProductionRate = (centsPerSecond) => {
  return formatMoneyCompact(centsPerSecond) + '/s';
};

/**
 * Format points (not money, so can use regular numbers)
 * @param {number} points - Number of points
 * @returns {string} Formatted points string
 */
export const formatPoints = (points) => {
  if (typeof points !== 'number' || isNaN(points)) return '0';
  return points.toLocaleString();
};

/**
 * Format points in compact form (1K, 1M, 1B, etc.)
 * @param {number|string} points - Number of points
 * @returns {string} Compact formatted points string
 */
const POINTS_TIERS = [
  [1e63, 'Vg'],  [1e60, 'NoDc'], [1e57, 'OcDc'], [1e54, 'SpDc'],
  [1e51, 'SxDc'], [1e48, 'QiDc'], [1e45, 'QaDc'], [1e42, 'TDc'],
  [1e39, 'DDc'],  [1e36, 'UDc'],  [1e33, 'Dc'],   [1e30, 'No'],
  [1e27, 'Oc'],   [1e24, 'Sp'],   [1e21, 'Sx'],   [1e18, 'Qi'],
  [1e15, 'Q'],    [1e12, 'T'],    [1e9,  'B'],    [1e6,  'M'],
  [1e3,  'K'],
];

export const formatPointsCompact = (points) => {
  const num = typeof points === 'string' ? parseFloat(points) : points;
  if (isNaN(num)) return '0';

  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  for (const [threshold, suffix] of POINTS_TIERS) {
    if (absNum >= threshold) {
      return sign + (absNum / threshold).toFixed(1) + suffix;
    }
  }

  return sign + Math.floor(absNum).toString();
};

// Default export for convenience
export default {
  parseMoney,
  parseMoneyToNumber,
  addMoney,
  subtractMoney,
  multiplyMoney,
  compareMoney,
  isPositive,
  hasSufficientBalance,
  formatMoney,
  formatMoneyCompact,
  formatChipLabel,
  formatBetInput,
  isValidBetAmount,
  formatMoneySmart,
  formatProductionRate,
  formatPoints,
  formatPointsCompact
};
